import { useCallback, type ReactNode } from 'react'
import React from 'react'
import { Divider } from 'antd'
import {
  VentureSection,
  NeedSection,
  StandaloneIdeasSection,
} from '../../../components/Report/ReportComponents'
import type { MindMapNode, MindMapNodeData, ProjectLabel, StakeholderRatings } from '@bandos/shared'
import type { SectionKey } from '../../reportSections'

interface ReportContentProps {
  ventureNodes: MindMapNode[]
  standaloneNeeds: MindMapNode[]
  standaloneIdeas: MindMapNode[]
  getEffectiveNodeData: (
    node: MindMapNode
  ) => MindMapNodeData & { stakeholderRating?: StakeholderRatings }
  getVentureNeeds: (id: string) => MindMapNode[]
  getChildIdeas: (parentId: string) => MindMapNode[]
  getLabelsForNode: (nodeId: string) => ProjectLabel[]
  renderIdeaExtras: (nodeId: string) => ReactNode
  isVisible: (key: SectionKey) => boolean
  getAllIdeasUnderVenture: (id: string) => MindMapNode[]
  ideasFromStandaloneNeeds: MindMapNode[]
  /** When provided, sections become clickable for branch selection */
  onNodeClick?: (e: React.MouseEvent, nodeId: string) => void
  /** Node IDs currently selected in branch selection mode */
  selectedNodeIds?: Set<string>
}

export function ReportContent({
  ventureNodes,
  standaloneNeeds,
  standaloneIdeas,
  getEffectiveNodeData,
  getVentureNeeds,
  getChildIdeas,
  getLabelsForNode,
  renderIdeaExtras,
  isVisible,
  getAllIdeasUnderVenture,
  ideasFromStandaloneNeeds,
  onNodeClick,
  selectedNodeIds,
}: ReportContentProps) {
  const getIdeaData = useCallback(
    (node: MindMapNode) => ({ label: getEffectiveNodeData(node).label }),
    [getEffectiveNodeData]
  )
  const getNodeId = useCallback((node: MindMapNode) => node.id, [])

  return (
    <>
      {/* 1. VENTURES with nested unmet needs and ideas */}
      {ventureNodes.map((ventureNode) => {
        const ventureData = getEffectiveNodeData(ventureNode)
        const childNeeds = getVentureNeeds(ventureNode.id)

        const childContent = isVisible('need')
          ? childNeeds.map((need) => {
              const needData = getEffectiveNodeData(need)
              return (
                <NeedSection
                  key={need.id}
                  nodeId={need.id}
                  data={{ label: needData.label }}
                  getChildIdeas={getChildIdeas}
                  getIdeaData={getIdeaData}
                  getNodeId={getNodeId}
                  getNodeLabels={getLabelsForNode}
                  renderNodeExtra={renderIdeaExtras}
                  renderIdeaExtra={renderIdeaExtras}
                  isIdeaVisible={isVisible('idea')}
                  onNodeClick={onNodeClick}
                  selected={selectedNodeIds?.has(need.id)}
                  selectedNodeIds={selectedNodeIds}
                />
              )
            })
          : /* needs hidden: show ideas/extras directly inside venture */
            getAllIdeasUnderVenture(ventureNode.id).length > 0 &&
            (isVisible('idea') || isVisible('storyboard') || isVisible('survey')) && (
              <StandaloneIdeasSection
                ideas={getAllIdeasUnderVenture(ventureNode.id)}
                getChildIdeas={getChildIdeas}
                getIdeaData={getIdeaData}
                getNodeId={getNodeId}
                getNodeLabels={getLabelsForNode}
                renderIdeaExtra={renderIdeaExtras}
                isIdeaVisible={isVisible('idea')}
                onNodeClick={onNodeClick}
                selectedNodeIds={selectedNodeIds}
              />
            )

        // When venture is hidden, render its children flat (preserves needs/ideas without venture chrome)
        if (!isVisible('venture')) {
          return <React.Fragment key={ventureNode.id}>{childContent}</React.Fragment>
        }

        return (
          <VentureSection
            key={ventureNode.id}
            nodeId={ventureNode.id}
            data={{
              companyName: ventureData.companyName,
              businessModelAndValueProp: ventureData.businessModelAndValueProp,
              goals: ventureData.goals,
              capabilitiesAndAssets: ventureData.capabilitiesAndAssets,
              productType: ventureData.productType,
              hardConstraints: ventureData.hardConstraints,
            }}
            renderNodeExtra={renderIdeaExtras}
            onNodeClick={onNodeClick}
            selected={selectedNodeIds?.has(ventureNode.id)}
          >
            {childContent}
          </VentureSection>
        )
      })}

      {/* 2. STANDALONE UNMET NEEDS (not under any venture) */}
      {isVisible('need') && standaloneNeeds.length > 0 && (
        <>
          {ventureNodes.length > 0 && <Divider />}
          {standaloneNeeds.map((need: MindMapNode) => {
            const needData = getEffectiveNodeData(need)
            return (
              <NeedSection
                key={need.id}
                nodeId={need.id}
                data={{ label: needData.label }}
                getChildIdeas={getChildIdeas}
                getIdeaData={getIdeaData}
                getNodeId={getNodeId}
                getNodeLabels={getLabelsForNode}
                renderNodeExtra={renderIdeaExtras}
                renderIdeaExtra={renderIdeaExtras}
                isIdeaVisible={isVisible('idea')}
                onNodeClick={onNodeClick}
                selected={selectedNodeIds?.has(need.id)}
                selectedNodeIds={selectedNodeIds}
              />
            )
          })}
        </>
      )}

      {/* 3. STANDALONE IDEAS (not under any need) */}
      {(isVisible('idea') || isVisible('storyboard') || isVisible('survey')) &&
        (standaloneIdeas.length > 0 || ideasFromStandaloneNeeds.length > 0) && (
          <>
            {ventureNodes.length > 0 || (standaloneNeeds.length > 0 && isVisible('need')) ? (
              <Divider />
            ) : null}
            <StandaloneIdeasSection
              ideas={[...standaloneIdeas, ...ideasFromStandaloneNeeds]}
              getChildIdeas={getChildIdeas}
              getIdeaData={getIdeaData}
              getNodeId={getNodeId}
              getNodeLabels={getLabelsForNode}
              renderIdeaExtra={renderIdeaExtras}
              isIdeaVisible={isVisible('idea')}
              onNodeClick={onNodeClick}
              selectedNodeIds={selectedNodeIds}
            />
          </>
        )}
    </>
  )
}
